import { computed, ref } from "vue";
import {
	getSubscriptionEndDate,
	subscriptionIsExpiredLast6Months,
	subscriptionIsExpiredRecently,
	subscriptionIsExpiring
} from "@/helpers/device-subscription.helper.js";
import { useLogMonitoring } from "@/plugins/user-console-monitoring/useLogMonitoring.js";
import DeviceService from "@/services/v1/Device.service.js";
import { useDayjs } from "@/plugins/dayjs/dayjs.plugin.js";

const { $dayjs } = useDayjs();
const devices = ref([]);

export const storageKeys = Object.freeze({
	EXPIRED: "HIDE_EXPIRED_BOX_NOTIFICATIONS",
	EXPIRING: "HIDE_EXPIRING_BOX_NOTIFICATIONS"
});

const expiringDevices = computed(() => {
	return devices.value
		.filter((box) => subscriptionIsExpiring(getSubscriptionEndDate(box)))
		.map((device) => {
			return {
				...device,
				remainingDaysBeforeExpired:
					$dayjs(
						device.deviceSubscription?.endDate,
						"YYYY-MM-DD"
					).diff($dayjs(), "day") + 1
			};
		})
		.sort(
			(firstDevice, secondDevice) =>
				firstDevice.remainingDaysBeforeExpired -
				secondDevice.remainingDaysBeforeExpired
		);
});

const expiredDevices = computed(() =>
	devices.value.filter((box) =>
		subscriptionIsExpiredLast6Months(getSubscriptionEndDate(box))
	)
);

const expiredRecentlyDevices = computed(() => {
	const devicesStores =
		localStorage.getItem(storageKeys.EXPIRED)?.split(",") || [];
	return devices.value.filter(
		(box) =>
			subscriptionIsExpiredRecently(getSubscriptionEndDate(box)) &&
			devicesStores.indexOf(box.deviceId) < 0
	);
});

export function useExpiredDevices() {
	const { notify } = useLogMonitoring();
	const displayExpired = computed(() => {
		const devicesStored =
			localStorage.getItem(storageKeys.EXPIRED)?.split(",") || [];

		return !!(
			// Has expired devices
			(
				expiredDevices.value.length > 0 &&
				// Has expired devices that haven't already been seen (and closed banner)
				expiredDevices.value.some(
					(device) => devicesStored.indexOf(device.deviceId) < 0
				) &&
				// Has expired devices less than 15 days
				expiredDevices.value.some((device) =>
					$dayjs(device.deviceSubscription?.endDate).isSameOrAfter(
						$dayjs().subtract(15, "day")
					)
				)
			)
		);
	});

	const displayExpiring = computed(() => {
		const devicesStored =
			localStorage.getItem(storageKeys.EXPIRING)?.split(",") || [];
		return !!(
			expiringDevices.value.length > 0 &&
			expiringDevices.value.some(
				(device) => devicesStored.indexOf(device.deviceId) < 0
			)
		);
	});

	/**
	 * Get devices by params or standalone by API
	 * @param {Array} _devices
	 * @returns
	 */
	const getDevices = async (_devices = false) => {
		if (_devices) {
			return (devices.value = _devices);
		}

		if (devices.value.length > 0) {
			return devices.value;
		}

		try {
			devices.value = await DeviceService.getDevices({
				includeChildOrganizations: true,
				showSpinner: false,
				includeHistory: true,
				includeSubscription: true,
				includeConnectivityStatus: true
			});
			return devices.value;
		} catch (error) {
			notify("[useExpiredDevices] Failed getting devices", error);
		}
	};

	function skipExpiration() {
		if (displayExpired.value) {
			localStorage.setItem(
				storageKeys.EXPIRED,
				expiredDevices.value.map((device) => device.deviceId)
			);
		} else if (displayExpiring.value) {
			localStorage.setItem(
				storageKeys.EXPIRING,
				expiringDevices.value.map((device) => device.deviceId)
			);
		}
	}

	return {
		getDevices,
		skipExpiration,
		devices,
		expiringDevices,
		displayExpiring,
		expiredDevices,
		expiredRecentlyDevices,
		displayExpired
	};
}
